import * as React from "react";
import { Maximize2, Minus, Plus, Radio } from "lucide-react";
import { fitFloorLabelSize } from "@eduviaos/shared";
import { cn } from "@eduviaos/ui-kit";
import type { PortalStrings } from "../../lib/i18n/usePortalLocale";
import { MAX_ZOOM, MIN_ZOOM, clampView, zoomAt, type View } from "./floorMapView";
import type {
  FloorPlan,
  FloorSeatPlacement,
  SeatAvailabilityItem,
  SeatRealtimeState,
} from "../../lib/seat/api";

/**
 * Phase SC-22c — the learner's floor map. The centre's saved floor plan
 * with each seat coloured free / taken / yours from the availability the
 * booking flow already reads (patched live by the seating websocket).
 *
 * Phones pinch to zoom and drag to pan once zoomed; desktops use the zoom
 * buttons or ctrl + wheel (also what a trackpad pinch sends). At 1× the
 * map lets the page scroll vertically, so it never traps a thumb. Every
 * seat is a keyboard button announced as "Seat A12, free".
 */

type SeatState = "free" | "taken" | "yours" | "selected";

const TAP_SLOP_PX = 6;

const SEAT_CLASSES: Record<SeatState, string> = {
  free: "fill-emerald-500/10 stroke-emerald-600/70 dark:stroke-emerald-400/70",
  taken: "fill-muted stroke-border",
  yours: "fill-primary/15 stroke-primary",
  selected: "fill-primary stroke-primary",
};

const SWATCH_CLASSES: Record<Exclude<SeatState, "selected">, string> = {
  free: "border-emerald-600/70 bg-emerald-500/10 dark:border-emerald-400/70",
  taken: "border-border bg-muted",
  yours: "border-primary bg-primary/15",
};

export function SeatFloorMap({
  plan,
  seats,
  selectedId,
  onSelect,
  realtime,
  strings,
}: {
  plan: FloorPlan;
  seats: SeatAvailabilityItem[];
  selectedId: string | null;
  onSelect: (seatId: string) => void;
  realtime: SeatRealtimeState;
  strings: PortalStrings;
}) {
  const svgRef = React.useRef<SVGSVGElement>(null);
  const [view, setView] = React.useState<View>({ k: 1, x: 0, y: 0 });
  const [animate, setAnimate] = React.useState(false);
  const pointers = React.useRef(new Map<number, { x: number; y: number }>());
  const gesture = React.useRef<{ moved: boolean; startDist: number; startView: View } | null>(null);
  const suppressClick = React.useRef(false);

  const byId = React.useMemo(() => new Map(seats.map((s) => [s.seat_id, s])), [seats]);
  // Seats this learner can't book here (a permanent room, another branch's
  // view) still draw, as taken, so the floor has no holes.
  const placed = plan.seats.filter((p) => p.seat_id);
  const freeCount = placed.filter((p) => byId.get(p.seat_id!)?.available).length;

  const stateOf = (placement: FloorSeatPlacement): SeatState => {
    const item = byId.get(placement.seat_id!);
    if (!item) return "taken";
    if (placement.seat_id === selectedId) return "selected";
    if (item.assigned_to_me) return "yours";
    return item.available ? "free" : "taken";
  };

  /** Client pixels → floor units, honouring the viewBox letterbox. */
  const toFloor = React.useCallback(
    (clientX: number, clientY: number) => {
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect || rect.width === 0 || rect.height === 0) return { x: 0, y: 0, scale: 1 };
      const scale = Math.max(plan.width / rect.width, plan.height / rect.height);
      const offsetX = (rect.width * scale - plan.width) / 2;
      const offsetY = (rect.height * scale - plan.height) / 2;
      return {
        x: (clientX - rect.left) * scale - offsetX,
        y: (clientY - rect.top) * scale - offsetY,
        scale,
      };
    },
    [plan.width, plan.height],
  );

  const setClamped = React.useCallback(
    (next: View, withAnimation: boolean) => {
      setAnimate(withAnimation);
      setView(clampView(next, plan.width, plan.height));
    },
    [plan.width, plan.height],
  );

  function zoomBy(factor: number) {
    const k = Math.min(Math.max(view.k * factor, MIN_ZOOM), MAX_ZOOM);
    setClamped(zoomAt(view, k, plan.width / 2, plan.height / 2), true);
  }

  function onPointerDown(e: React.PointerEvent<SVGSVGElement>) {
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointers.current.values()];
    gesture.current = {
      moved: false,
      startDist: pts.length >= 2 ? Math.hypot(pts[0]!.x - pts[1]!.x, pts[0]!.y - pts[1]!.y) : 0,
      startView: view,
    };
  }

  function onPointerMove(e: React.PointerEvent<SVGSVGElement>) {
    const prev = pointers.current.get(e.pointerId);
    const g = gesture.current;
    if (!prev || !g) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointers.current.values()];

    if (pts.length >= 2 && g.startDist > 0) {
      const dist = Math.hypot(pts[0]!.x - pts[1]!.x, pts[0]!.y - pts[1]!.y);
      const mid = toFloor((pts[0]!.x + pts[1]!.x) / 2, (pts[0]!.y + pts[1]!.y) / 2);
      const k = Math.min(Math.max(g.startView.k * (dist / g.startDist), MIN_ZOOM), MAX_ZOOM);
      g.moved = true;
      setClamped(zoomAt(g.startView, k, mid.x, mid.y), false);
      return;
    }

    if (view.k <= 1) return;
    const dx = e.clientX - prev.x;
    const dy = e.clientY - prev.y;
    if (!g.moved && Math.hypot(dx, dy) < TAP_SLOP_PX) {
      pointers.current.set(e.pointerId, prev);
      return;
    }
    if (!g.moved) {
      g.moved = true;
      e.currentTarget.setPointerCapture?.(e.pointerId);
    }
    const { scale } = toFloor(e.clientX, e.clientY);
    setClamped({ k: view.k, x: view.x + dx * scale, y: view.y + dy * scale }, false);
  }

  function onPointerEnd(e: React.PointerEvent<SVGSVGElement>) {
    pointers.current.delete(e.pointerId);
    if (gesture.current?.moved) suppressClick.current = true;
    if (pointers.current.size === 0) {
      gesture.current = null;
      window.setTimeout(() => {
        suppressClick.current = false;
      }, 0);
    } else if (gesture.current) {
      gesture.current = { moved: true, startDist: 0, startView: view };
    }
  }

  React.useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      const point = toFloor(e.clientX, e.clientY);
      setView((current) => {
        const k = Math.min(Math.max(current.k * Math.exp(-e.deltaY / 200), MIN_ZOOM), MAX_ZOOM);
        return clampView(zoomAt(current, k, point.x, point.y), plan.width, plan.height);
      });
      setAnimate(false);
    };
    svg.addEventListener("wheel", onWheel, { passive: false });
    return () => svg.removeEventListener("wheel", onWheel);
  }, [toFloor, plan.width, plan.height]);

  function choose(placement: FloorSeatPlacement) {
    if (suppressClick.current) return;
    const state = stateOf(placement);
    if (state === "taken") return;
    onSelect(placement.seat_id!);
  }

  const stateLabel: Record<SeatState, string> = {
    free: strings.floorSeatFree,
    taken: strings.floorSeatTaken,
    yours: strings.floorSeatYours,
    selected: strings.floorSeatSelected,
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
          {strings.floorMapTitle}
        </p>
        <span
          className={cn(
            "inline-flex items-center gap-1 text-xs",
            realtime === "live"
              ? "text-emerald-700 dark:text-emerald-300"
              : "text-muted-foreground",
          )}
          data-testid="floor-map-transport"
        >
          <Radio aria-hidden="true" className="size-3" />
          {realtime === "live" ? strings.floorMapLive : strings.floorMapPolling}
        </span>
      </div>

      <div className="border-border/60 bg-muted/20 relative overflow-hidden rounded-xl border">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${plan.width} ${plan.height}`}
          className="block h-auto max-h-[60vh] w-full select-none"
          style={{ touchAction: view.k > 1 ? "none" : "pan-y" }}
          role="group"
          aria-label={strings.floorMapAria(plan.name, freeCount, placed.length)}
          data-testid="seat-floor-map"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerEnd}
          onPointerCancel={onPointerEnd}
        >
          <g
            style={{
              transform: `translate(${view.x}px, ${view.y}px) scale(${view.k})`,
              transformOrigin: "0 0",
            }}
            className={cn(
              animate && "transition-transform duration-200 ease-out motion-reduce:transition-none",
            )}
            data-testid="seat-floor-map-viewport"
            data-zoom={view.k.toFixed(2)}
          >
            <rect
              x={0}
              y={0}
              width={plan.width}
              height={plan.height}
              rx={2}
              className="fill-card stroke-border"
              strokeWidth={0.4}
            />
            {plan.zones.map((zone, i) => (
              <g key={zone.zone_id ?? `zone-${i}`} aria-hidden="true">
                <rect
                  x={zone.x}
                  y={zone.y}
                  width={zone.w}
                  height={zone.h}
                  rx={1.5}
                  className="fill-primary/5 stroke-primary/30"
                  strokeWidth={0.4}
                  strokeDasharray="2 1.5"
                />
                <text
                  x={zone.x + 2}
                  y={zone.y + 4.5}
                  fontSize={3.4}
                  className="fill-muted-foreground font-semibold"
                >
                  {zone.label}
                </text>
              </g>
            ))}
            {plan.features.map((f, i) => {
              const fontSize = f.label
                ? fitFloorLabelSize(f.label, f.w, f.h, { max: 2.6, min: 1.6 })
                : null;
              return (
                <g
                  key={`feature-${i}`}
                  aria-hidden="true"
                  transform={
                    f.rotation
                      ? `rotate(${f.rotation} ${f.x + f.w / 2} ${f.y + f.h / 2})`
                      : undefined
                  }
                >
                  <rect x={f.x} y={f.y} width={f.w} height={f.h} rx={0.8} className="fill-muted" />
                  {f.label && fontSize !== null ? (
                    <text
                      x={f.x + f.w / 2}
                      y={f.y + f.h / 2}
                      fontSize={fontSize}
                      textAnchor="middle"
                      dominantBaseline="central"
                      className="fill-muted-foreground"
                    >
                      {f.label}
                    </text>
                  ) : null}
                </g>
              );
            })}
            {placed.map((p) => {
              const state = stateOf(p);
              const disabled = state === "taken";
              return (
                <g
                  key={p.seat_id}
                  role="button"
                  tabIndex={disabled ? -1 : 0}
                  aria-label={strings.floorSeatAria(p.seat_number, stateLabel[state])}
                  aria-pressed={state === "selected"}
                  aria-disabled={disabled || undefined}
                  data-testid={`floor-seat-${p.seat_number}`}
                  data-state={state}
                  transform={
                    p.rotation
                      ? `rotate(${p.rotation} ${p.x + p.w / 2} ${p.y + p.h / 2})`
                      : undefined
                  }
                  className={cn(
                    "outline-none [&:focus-visible>rect]:stroke-[1.4]",
                    disabled ? "cursor-not-allowed" : "cursor-pointer",
                  )}
                  onClick={() => choose(p)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      choose(p);
                    }
                  }}
                >
                  <rect
                    x={p.x}
                    y={p.y}
                    width={p.w}
                    height={p.h}
                    rx={1.4}
                    strokeWidth={0.6}
                    className={cn(
                      "transition-[fill,stroke] duration-200 ease-out motion-reduce:transition-none",
                      SEAT_CLASSES[state],
                    )}
                  />
                  <text
                    x={p.x + p.w / 2}
                    y={p.y + p.h / 2}
                    fontSize={Math.min(p.h * 0.42, 3.6)}
                    textAnchor="middle"
                    dominantBaseline="central"
                    className={cn(
                      "pointer-events-none font-semibold tabular-nums",
                      state === "selected" ? "fill-primary-foreground" : "fill-foreground",
                      state === "taken" && "fill-muted-foreground",
                    )}
                  >
                    {p.seat_number}
                  </text>
                </g>
              );
            })}
          </g>
        </svg>

        <div className="absolute bottom-2 right-2 flex flex-col gap-1">
          <MapButton
            label={strings.floorMapZoomIn}
            onClick={() => zoomBy(1.5)}
            disabled={view.k >= MAX_ZOOM}
          >
            <Plus className="size-4" />
          </MapButton>
          <MapButton
            label={strings.floorMapZoomOut}
            onClick={() => zoomBy(1 / 1.5)}
            disabled={view.k <= MIN_ZOOM}
          >
            <Minus className="size-4" />
          </MapButton>
          <MapButton
            label={strings.floorMapReset}
            onClick={() => setClamped({ k: 1, x: 0, y: 0 }, true)}
            disabled={view.k === 1}
          >
            <Maximize2 className="size-3.5" />
          </MapButton>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
        {(Object.keys(SWATCH_CLASSES) as Array<keyof typeof SWATCH_CLASSES>).map((state) => (
          <span
            key={state}
            className="text-muted-foreground inline-flex items-center gap-1.5 text-xs"
          >
            <span
              aria-hidden="true"
              className={cn("size-3 rounded-[4px] border", SWATCH_CLASSES[state])}
            />
            {stateLabel[state]}
          </span>
        ))}
      </div>
      <p className="text-muted-foreground text-xs">{strings.floorMapHint}</p>
    </div>
  );
}

function MapButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className="border-border/60 bg-card/95 text-foreground hover:bg-muted grid size-9 place-items-center rounded-lg border shadow-sm backdrop-blur transition-[background-color,transform] duration-150 ease-out active:scale-[0.97] disabled:opacity-40 motion-reduce:transition-none"
    >
      {children}
    </button>
  );
}
